import { useClickOutside } from '@mantine/hooks';
import { CloseButton } from '@zorro/design-system/v2/CloseButton';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Popover } from '@zorro/design-system/v2/Popover';
import { Title } from '@zorro/design-system/v2/Title';
import { CommentIcon } from '@zorro/shared/assets';
import { Box, Group, Stack } from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';

import { Comments } from './Comments';
import { CommentsButton } from './CommentsButton';
import { useEnrollmentComments } from './useEnrollmentComments';

type Props = {
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
};

export const CommentsPopover = ({
  onboardingPeriodId,
  employerId,
  employeeId,
}: Props) => {
  const { isManagement } = useRoles();
  const { showComments, setShowComments, selectedCommentId } =
    useEnrollmentComments();

  const ref = useClickOutside(() => setShowComments(false));

  const { data: comments = [] } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindManyEnrollmentComments',
    params: [onboardingPeriodId],
    enabled: isManagement,
  });

  if (!isManagement) {
    return null;
  }

  return (
    <Popover position="left-start" opened={showComments} shadow="md" withArrow>
      <Popover.Target>
        <Box pos="relative">
          <CommentsButton
            onClick={() => setShowComments(true)}
            commentsCount={comments.length}
          />
        </Box>
      </Popover.Target>
      <Popover.Dropdown ref={ref}>
        <Stack h="34rem" w="38rem">
          <Group justify="space-between" align="center">
            <Group gap="xs" pt="sm">
              <CommentIcon
                style={{ paddingLeft: '1.25rem', marginTop: '0.25rem' }}
                count={comments.length}
              />
              <Title order={3}>Comments</Title>
            </Group>
            <Box style={{ marginTop: '0.5rem' }}>
              <CloseButton onClick={() => setShowComments(false)} />
            </Box>
          </Group>
          <Divider />
          <Comments
            onboardingPeriodId={onboardingPeriodId}
            selectedCommentId={selectedCommentId}
            employerId={employerId}
            employeeId={employeeId}
            px="0.25rem"
            editorPy="xs"
            sendButtonAlign="center"
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
